
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  publishedAt: string;
  updatedAt: string;
  author: string;
  authorImage?: string;
  authorTitle?: string;
  category: string;
  coverImage?: string;
  tags?: string[];
  readTime: number;
}

export const useBlogPosts = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  useEffect(() => {
    const fetchPosts = async () => {
      setIsLoading(true);
      try {
        // This would normally fetch from Supabase, but for now we're using sample data
        // Example implementation for when the blog_posts table is created:
        /*
        const { data, error } = await supabase
          .from('blog_posts')
          .select('*')
          .order('published_at', { ascending: false });
          
        if (error) throw error;
        setPosts(data.map(post => ({
          id: post.id,
          title: post.title,
          slug: post.slug,
          excerpt: post.excerpt,
          content: post.content,
          publishedAt: post.published_at,
          updatedAt: post.updated_at,
          author: post.author,
          authorImage: post.author_image,
          authorTitle: post.author_title,
          category: post.category,
          coverImage: post.cover_image,
          tags: post.tags,
          readTime: post.read_time
        })));
        */
        
        // For demo purposes, use sample data
        setTimeout(() => {
          setPosts(sampleBlogPosts);
          setIsLoading(false);
        }, 500);
        
      } catch (err) {
        console.error("Error fetching blog posts:", err);
        setError(err as Error);
        setIsLoading(false);
      }
    };
    
    fetchPosts();
  }, []);
  
  return {
    posts,
    isLoading,
    error
  };
};

// Sample data for initial development
const sampleBlogPosts: BlogPost[] = [
  {
    id: '1',
    title: 'Mastering Chain of Thought Prompting',
    slug: 'mastering-chain-of-thought-prompting',
    excerpt: 'Learn how to use chain of thought techniques to improve reasoning in large language models.',
    content: `
      <p>Chain of Thought (CoT) prompting is a technique that enhances the reasoning capabilities of large language models by encouraging them to break down complex problems into intermediate steps.</p>
      <h2>Why Chain of Thought Works</h2>
      <p>By explicitly asking the model to "think step by step," you tap into its ability to solve problems methodically rather than jumping directly to conclusions. This approach mimics human reasoning and produces more accurate results, especially for tasks that require multi-step reasoning like math problems or logical deductions.</p>
      <h2>Basic Implementation</h2>
      <p>To implement CoT prompting, simply add phrases like "Let's think through this step by step" or "Let's work through this methodically" to your prompts. This simple addition can significantly improve the quality of responses.</p>
      <h2>Example</h2>
      <pre><code>Without CoT: What is 17 × 23?
Response: 17 × 23 = 391</code></pre>
      <pre><code>With CoT: What is 17 × 23? Think step by step.
Response: 
To calculate 17 × 23, I'll break it down:
1. First, let's multiply 17 × 20 = 340
2. Next, let's multiply 17 × 3 = 51
3. Now, let's add these results: 340 + 51 = 391
Therefore, 17 × 23 = 391</code></pre>
      <h2>Advanced Applications</h2>
      <p>Chain of Thought prompting is particularly effective for:</p>
      <ul>
        <li>Complex mathematical calculations</li>
        <li>Logical reasoning and deductions</li>
        <li>Troubleshooting technical problems</li>
        <li>Breaking down complex processes into manageable steps</li>
      </ul>
      <p>For maximum effectiveness, consider combining CoT with other techniques like Zero-Shot or Few-Shot prompting, creating a powerful toolkit for advanced problem-solving with LLMs.</p>
    `,
    publishedAt: '2024-04-01',
    updatedAt: '2024-04-01',
    author: 'Alex Johnson',
    authorTitle: 'Prompt Engineering Lead',
    category: 'Tutorials',
    coverImage: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
    tags: ['Chain of Thought', 'Reasoning', 'Problem Solving', 'Advanced Techniques'],
    readTime: 8
  },
  {
    id: '2',
    title: 'GPT-4 vs Claude 3: A Comprehensive Comparison',
    slug: 'gpt4-vs-claude3-comparison',
    excerpt: 'An in-depth analysis of the strengths and weaknesses of OpenAI\'s GPT-4 and Anthropic\'s Claude 3.',
    content: '<p>This is a placeholder for the full article content.</p>',
    publishedAt: '2024-03-28',
    updatedAt: '2024-03-30',
    author: 'Maria Chen',
    authorTitle: 'AI Research Analyst',
    category: 'Model Updates',
    coverImage: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
    tags: ['GPT-4', 'Claude 3', 'Model Comparison', 'Benchmarks'],
    readTime: 12
  },
  {
    id: '3',
    title: 'Creating Effective Persona Prompts',
    slug: 'creating-effective-persona-prompts',
    excerpt: 'Learn how to craft compelling personas that elicit the exact response style you need from AI models.',
    content: '<p>This is a placeholder for the full article content.</p>',
    publishedAt: '2024-03-25',
    updatedAt: '2024-03-25',
    author: 'James Wilson',
    category: 'Best Practices',
    coverImage: 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
    tags: ['Personas', 'Role Playing', 'Tone Control'],
    readTime: 6
  },
  {
    id: '4',
    title: 'How a Finance Team Reduced Report Creation Time by 80% Using Prompt Engineering',
    slug: 'finance-team-case-study',
    excerpt: 'A case study of how a Fortune 500 company\'s finance department revolutionized their workflow with AI.',
    content: '<p>This is a placeholder for the full article content.</p>',
    publishedAt: '2024-03-20',
    updatedAt: '2024-03-22',
    author: 'Sarah Rodriguez',
    authorTitle: 'Business AI Consultant',
    category: 'Case Studies',
    coverImage: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2036&q=80',
    tags: ['Finance', 'Case Study', 'Productivity', 'Business'],
    readTime: 10
  },
  {
    id: '5',
    title: 'Monthly Contest Winners: Best Marketing Prompts',
    slug: 'march-contest-winners',
    excerpt: 'Celebrating our community\'s most creative and effective marketing prompts from this month\'s competition.',
    content: '<p>This is a placeholder for the full article content.</p>',
    publishedAt: '2024-03-15',
    updatedAt: '2024-03-15',
    author: 'Community Team',
    category: 'Community',
    coverImage: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2074&q=80',
    tags: ['Contest', 'Marketing', 'Community', 'Creativity'],
    readTime: 7
  }
];
